/**
 * sheetLayout — desktop-aware bottom-sheet layout helper.
 *
 * Mobile (native + web < 1024px): every sheet renders as a bottom-pinned
 * slide-up sheet, exactly as it does today. The hook returns empty style
 * overrides and `showHandle: true` so existing styles win unchanged.
 *
 * Desktop web (≥ 1024px): bottom sheets are visually wrong — they render as
 * an awkward slab pinned to the bottom of a wide browser window. The hook
 * returns overrides that re-center the sheet, cap its width/height, round
 * all four corners, hide the drag handle, and switch the Modal animation
 * from 'slide' to 'fade'.
 *
 * Tablets (web 768–1023px) are intentionally treated as mobile per product
 * decision — the bottom-sheet feel is preserved on tablet-sized browser tabs.
 *
 * Usage (Pattern A — overlay + sheet wrapping):
 *
 *   const sheet = useSheetLayout({ desktopMaxWidth: 560 });
 *   return (
 *     <Modal
 *       visible={visible}
 *       transparent
 *       animationType={sheet.animationType}
 *       onRequestClose={onClose}
 *     >
 *       <Pressable style={[styles.overlay, sheet.overlayStyle]} onPress={onClose}>
 *         <Pressable style={[styles.sheet, sheet.sheetStyle]} onPress={() => {}}>
 *           {sheet.showHandle && <View style={styles.handle} />}
 *           ...content...
 *         </Pressable>
 *       </Pressable>
 *     </Modal>
 *   );
 *
 * Pattern B (KeyboardAvoidingView wrapper around backdrop) and
 * Pattern C (sibling overlay + sheet inside Modal) — same helper, just
 * apply `overlayStyle` to whichever element holds the dark backdrop and
 * `sheetStyle` to the sheet container.
 */

import { useWindowDimensions } from 'react-native';
import type { ViewStyle } from 'react-native';
import { isWeb, DESKTOP_BREAKPOINT } from './platform';

interface SheetLayoutOptions {
  /** Max width on desktop. Default 560. */
  desktopMaxWidth?: number;
  /**
   * Minimum width on desktop. Defaults to `min(desktopMaxWidth, 380)`.
   * The floor exists because some consumers wrap the sheet in an
   * intermediate `flex: 1` view (e.g. for KeyboardAvoidingView), which
   * combined with the overlay's `alignItems: 'center'` can collapse the
   * card to its intrinsic content width — undesirable for read-heavy
   * detail panels (medication history, snapshot info, etc.). The floor
   * keeps such cards readable even when the structural fix lives one
   * level up.
   */
  desktopMinWidth?: number;
  /**
   * Max height on desktop, as a fraction of viewport height (0..1).
   * Default 0.86.
   */
  desktopMaxHeightRatio?: number;
  /** Border radius on desktop (all four corners). Default 16. */
  desktopBorderRadius?: number;
  /**
   * Inner content padding the helper should suggest on desktop. Most
   * existing sheets rely on the drag handle for ~22px of top breathing
   * room, but desktop hides the handle and floats the sheet inside a
   * viewport — so 14–16px gutters read as cramped at desktop max-widths.
   * Spread `desktopContentStyle` onto the card to opt in. Mobile
   * returns an empty object so existing card padding is unchanged.
   * Defaults: top 22, horizontal 24. paddingBottom is intentionally NOT
   * overridden — most sheets use a deliberately large bottom padding for
   * CTAs / safe-area insets, and replacing that wholesale regresses the
   * mobile-derived layout.
   */
  desktopContentPadding?: {
    top?: number;
    horizontal?: number;
  };
}

export interface SheetLayout {
  /** True only on web at ≥ DESKTOP_BREAKPOINT. */
  isDesktop: boolean;
  /**
   * Style override to spread onto the dark-backdrop container after the
   * existing styles. On mobile this is empty; on desktop it re-centers
   * the sheet via `justifyContent: 'center'`, `alignItems: 'center'`,
   * and adds outer padding so the sheet doesn't touch the viewport edge.
   */
  overlayStyle: ViewStyle;
  /**
   * Style override to spread onto the sheet container after the existing
   * styles. On mobile this is empty; on desktop it caps width/height,
   * rounds all four corners (overriding any `borderTopLeftRadius`/
   * `borderTopRightRadius` set by the sheet's own styles), and adds
   * `overflow: 'hidden'` so the rounded corners clip content.
   */
  sheetStyle: ViewStyle;
  /**
   * Optional inner-padding style for the sheet's content container.
   * Empty on mobile (consumers keep their existing padding). On desktop
   * it returns generous padding so content doesn't hug the rounded
   * card edges once the drag handle is hidden. Spread it onto the card
   * AFTER `sheetStyle` so it wins over the consumer's mobile padding
   * declared earlier in the style array.
   */
  desktopContentStyle: ViewStyle;
  /**
   * Top-only spacing replacement for the hidden drag handle. Empty on
   * mobile (the handle's own marginTop/marginBottom provides ~24px of
   * breathing room). On desktop returns `{ paddingTop: 22 }` so the
   * sheet's first child (typically a title) doesn't sit flush against
   * the rounded top edge after `showHandle` becomes false. Spread on
   * the sheet container for sheets whose inner views already declare
   * their own `paddingHorizontal` — applying full `desktopContentStyle`
   * would double-pad horizontally in that case.
   */
  topSpacingStyle: ViewStyle;
  /** Animation type to pass to the Modal: 'slide' on mobile, 'fade' on desktop. */
  animationType: 'slide' | 'fade';
  /** Whether to render the drag handle (always false on desktop). */
  showHandle: boolean;
}

const DEFAULT_DESKTOP_MAX_WIDTH = 560;
const DEFAULT_DESKTOP_MIN_WIDTH = 380;
const DEFAULT_DESKTOP_MAX_HEIGHT_RATIO = 0.86;
const DEFAULT_DESKTOP_BORDER_RADIUS = 16;
const DESKTOP_OVERLAY_PADDING = 16;
const DEFAULT_DESKTOP_CONTENT_PADDING_TOP = 22;
const DEFAULT_DESKTOP_CONTENT_PADDING_HORIZONTAL = 24;

export function useSheetLayout(options: SheetLayoutOptions = {}): SheetLayout {
  const {
    desktopMaxWidth = DEFAULT_DESKTOP_MAX_WIDTH,
    desktopMinWidth,
    desktopMaxHeightRatio = DEFAULT_DESKTOP_MAX_HEIGHT_RATIO,
    desktopBorderRadius = DEFAULT_DESKTOP_BORDER_RADIUS,
    desktopContentPadding,
  } = options;

  const { width, height } = useWindowDimensions();
  const isDesktop = isWeb && width >= DESKTOP_BREAKPOINT;

  if (!isDesktop) {
    return {
      isDesktop: false,
      overlayStyle: {},
      sheetStyle: {},
      desktopContentStyle: {},
      topSpacingStyle: {},
      animationType: 'slide',
      showHandle: true,
    };
  }

  const safeRatio = Math.max(0.3, Math.min(0.95, desktopMaxHeightRatio));
  const computedMaxHeight = Number.isFinite(height) && height > 0
    ? Math.floor(height * safeRatio)
    : 720;

  // Floor the sheet's width on desktop. Defaults to 380, capped at the
  // sheet's own maxWidth so a small popover (e.g. 320-wide) doesn't end up
  // with minWidth > maxWidth (which would force the sheet wider than its
  // declared cap). Also capped at the available viewport width minus
  // overlay padding so we never demand more space than exists.
  const availableWidth = Number.isFinite(width) && width > 0
    ? Math.max(0, width - DESKTOP_OVERLAY_PADDING * 2)
    : Infinity;
  const computedMinWidth = Math.min(
    desktopMinWidth ?? DEFAULT_DESKTOP_MIN_WIDTH,
    desktopMaxWidth,
    availableWidth,
  );

  const padTop = desktopContentPadding?.top ?? DEFAULT_DESKTOP_CONTENT_PADDING_TOP;
  const padHorizontal = desktopContentPadding?.horizontal ?? DEFAULT_DESKTOP_CONTENT_PADDING_HORIZONTAL;

  return {
    isDesktop: true,
    overlayStyle: {
      justifyContent: 'center',
      alignItems: 'center',
      padding: DESKTOP_OVERLAY_PADDING,
    },
    // paddingLeft/Right (not paddingHorizontal) so this reliably overrides
    // a consumer style that already declared `paddingHorizontal` — RN's
    // style flattening expands the shorthand and the long-hand here wins.
    // paddingBottom is intentionally left to the consumer; sheets that
    // pin a CTA / Done button typically use 28–40px and shouldn't be
    // shrunk on desktop.
    desktopContentStyle: {
      paddingTop: padTop,
      paddingLeft: padHorizontal,
      paddingRight: padHorizontal,
    },
    topSpacingStyle: {
      paddingTop: padTop,
    },
    sheetStyle: {
      // Many existing consumers anchor their sheet to the bottom of the
      // viewport on mobile via `position: 'absolute'` + `bottom/left/right: 0`.
      // On desktop, that pins the sheet to the bottom-left corner of the
      // backdrop and the parent's flex centering can't pull it back. Reset
      // those properties here so a single helper change re-centers every
      // existing sheet without each consumer having to duplicate the logic.
      position: 'relative',
      bottom: 'auto' as unknown as number,
      left: 'auto' as unknown as number,
      right: 'auto' as unknown as number,
      width: '100%',
      // minWidth is a defensive floor: some consumers wrap the sheet in an
      // intermediate `flex: 1` view (e.g. for KeyboardAvoidingView). Combined
      // with the overlay's `alignItems: 'center'`, that wrapper can collapse
      // to intrinsic content width on RN-Web, dragging the sheet's
      // `width: 100%` down with it. The floor keeps detail/history panels
      // readable even when the structural fix lives one level up. We do
      // NOT use alignSelf: 'stretch' here — combined with maxWidth it would
      // cross-axis-start-align the card (i.e. left-pin instead of center).
      minWidth: computedMinWidth,
      maxWidth: desktopMaxWidth,
      maxHeight: computedMaxHeight,
      borderTopLeftRadius: desktopBorderRadius,
      borderTopRightRadius: desktopBorderRadius,
      borderBottomLeftRadius: desktopBorderRadius,
      borderBottomRightRadius: desktopBorderRadius,
      overflow: 'hidden',
    },
    animationType: 'fade',
    showHandle: false,
  };
}
